<template>
  <div>
    <ion-item lines="none">
      <ion-item class="search">
        <ion-label position="floating">
          Search
        </ion-label>
        <ion-input-vue v-model="search" />
      </ion-item>

      <ion-button
        slot="end"
        @click="addVirtualConverter"
      >
        <ion-icon :src="i.add" />
        Create Virtual Converter
      </ion-button>
    </ion-item>

    <v-data-table
      :headers="headers"
      :items="devices"
      :items-per-page="50"
      :search="search"
      :loading="busy"
      :footer-props="{'items-per-page-options':[15, 30, 50, 100, -1]}"
    >
      <template v-slot:item.lastSeen="{ item }">
        {{ item.lastSeen | datetime }}
      </template>
      <template v-slot:item.measurements="{ item }">
        <template v-if="!isGateway(item)">
          <ion-badge
            v-for="(measurement, index) in getMeasurements(item)"
            :key="index"
            :title="measurement.type"
            class="ion-margin-end"
            :class="`sensor-${measurement.type}`"
          >
            {{ measurement | sensorMeasurement }}
          </ion-badge>
        </template>
        <ion-badge v-else>
          {{ item.installStatus }}
        </ion-badge>
      </template>

      <template v-slot:item.actions="{ item }">
        <ion-button
          v-if="assignableTypes.includes(item.type)"
          :disabled="item.busy"
          @click="assign(item)"
        >
          <ion-spinner v-if="item.busy" />
          <template v-else>
            Assign
          </template>
        </ion-button>
        <template v-else-if="isGateway(item)">
          <ion-button
            :disabled="gatewayIsBeingUpdated(item)"
            @click="reconfigureGateway(item)"
          >
            <ion-spinner v-if="item.busy" />
            <template v-else>
              Reconfigure
            </template>
          </ion-button>
          <ion-button
            v-if="gatewayFirmwareCanBeUpdated(item)"
            :disabled="gatewayIsBeingUpdated(item)"
            @click="updateFirmware(item)"
          >
            <ion-spinner v-if="item.busy" />
            <template v-else>
              Update firmware
            </template>
          </ion-button>
        </template>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Vue from 'vue'

import { VDataTable } from 'vuetify/lib'
import { IonInputVue } from '@modus/ionic-vue'
import { modalController } from '@modus/ionic-vue'

import Device from './Device'
import {mapGetters} from 'vuex';
import AddVirtualConverterModal from "./AddVirtualConverterModal";
import ReconfigureGateway from './ReconfigureGateway'
import UpdateGatewayFirmware from './UpdateGatewayFirmware'

export default {
  components: {
    VDataTable,
    IonInputVue
  },
  data() {
    return {
      serialNumber: this.$route.params.serialNumber,
      search: '',
      busy: false,
      assignableTypes: [
        'GROSENS',
        'ELSYS_CO2',
        'PAR',
        'ELSYS_ELT_2',
        'ELSYS_ERS_LITE',
        'TEROS_12',
        'CP_CO2',
        'CP_TH',
        'SMARTBOX_ASSOCIATE',
        'AME_ROOTZONE',
        'AME_AMBIENT',
        'AME_AMBIENT_CO2',
        'CP_CO2C',
      ]
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin'
    ]),
    bridge() {
      return this.$store.getters.getBridgeBySerial(this.serialNumber)
    },
    devices() {
      return this.bridge.devices
    },
    headers() {
      let headers = [
        {
          text: 'Serial number',
          value: 'serialNumber'
        },
        {
          text: 'Type',
          value: 'type'
        }
      ]

      if (this.isAdmin) {
        headers.push({
          text: 'DevEUI',
          value: 'hardwareAddress'
        })
      }

      headers.push({
        text: 'Recent measurements',
        value: 'measurements',
        sortable: false
      })

      headers.push({
        text: 'Last Seen',
        value: 'lastSeen'
      })
      headers.push({
        value: 'actions',
        sortable: false
      })

      return headers
    }
  },
  created() {
    const serialNumber = this.serialNumber

    this.busy = true

    this.$store.dispatch('fetchDevices', { serialNumber })
      .finally(() => this.busy = false)
  },
  methods: {
    getMeasurements(device){
      if(!device.sensors) return []
      return device.sensors.map(sensor => {
        if(!sensor.measurements || !sensor.measurements.recent) return null
        return sensor.measurements.recent
      }).filter(measurements => !!measurements)
    },
    isGateway(device){
      return 'RAK7240' === device.type;
    },
    gatewayFirmwareCanBeUpdated(device){
      return this.isGateway(device) &&
          device.firmwareVersion !== '1.2.1_RAK rRAK-544bc84'
    },
    gatewayIsBeingUpdated(device){
      return this.isGateway(device) &&
          device.installStatus === 'INSTALLING'
    },
    async reconfigureGateway(device) {
      if (!this.isGateway(device)) return
      const modal = await modalController.create({
        component: ReconfigureGateway,
        componentProps: {
          propsData: {
            serialNumber: this.serialNumber,
            gateway: device
          },
        },
      })

      await modal.present()
    },
    async updateFirmware(device) {
      if (!this.isGateway(device)) return
      const modal = await modalController.create({
        component: UpdateGatewayFirmware,
        componentProps: {
          propsData: {
            serialNumber: this.serialNumber,
            gateway: device
          },
        },
      })

      await modal.present()
    },
    async assign(device) {
      const serialNumber = this.serialNumber
      const deviceSerialNumber = device.serialNumber

      Vue.set(device, 'busy', true)

      const modal = await modalController
        .create({
          component: Device,
          componentProps: {
            propsData: {
              serialNumber,
              deviceSerialNumber
            }
          },
          cssClass: 'update-device-modal'
        })

      this.$store.dispatch('fetchDevice', { serialNumber, deviceSerialNumber })
        .then(() => modal.present())
        .finally(() => device.busy = false)
    },
    async addVirtualConverter() {
      const modal = await modalController.create({
        component: AddVirtualConverterModal
      })
      await modal.present()
      await modal.onDidDismiss()
      this.busy = true
      this.$store.dispatch('fetchDevices', { serialNumber: this.serialNumber })
          .finally(() => this.busy = false)
    }
  }
}
</script>
